import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllUsers, createUser, updateUserStatus, resetUserPassword } from '../api/thamanService';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';

export default function EmployeeDashboardPage() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    // Create user form
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'employee' });
    const [formError, setFormError] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    // Reset password modal
    const [resetTarget, setResetTarget] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetError, setResetError] = useState('');

    const loadUsers = useCallback(async () => {
        try {
            setError(null);
            setIsLoading(true);
            const data = await fetchAllUsers();
            setUsers(data);
        } catch (err) {
            setError('Failed to fetch users.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError('');
        if (newUser.password !== newUser.confirmPassword) {
            setFormError('Passwords do not match.');
            return;
        }
        if (newUser.password.length < 6) {
            setFormError('Password must be at least 6 characters.');
            return;
        }
        setIsCreating(true);
        try {
            await createUser({ name: newUser.name, email: newUser.email, password: newUser.password, role: newUser.role });
            setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 'employee' });
            setMessage('User created successfully.');
            await loadUsers();
        } catch (err) {
            setFormError(err.message || 'Failed to create user.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleToggleStatus = async (user) => {
        const newStatus = user.status === 'active' ? 'inactive' : 'active';
        if (!window.confirm(`${newStatus === 'inactive' ? 'Deactivate' : 'Activate'} ${user.name}?`)) return;
        try {
            await updateUserStatus(user.id, newStatus);
            setMessage(`User ${newStatus === 'inactive' ? 'deactivated' : 'activated'}.`);
            await loadUsers();
        } catch (err) {
            setError(err.message || 'Failed to update status.');
        }
    };

    const handleResetPassword = async () => {
        setResetError('');
        if (newPassword !== confirmNewPassword) { setResetError('Passwords do not match.'); return; }
        if (newPassword.length < 6) { setResetError('Password must be at least 6 characters.'); return; }
        try {
            await resetUserPassword(resetTarget.id, newPassword);
            setMessage(`Password reset for ${resetTarget.name}.`);
            setResetTarget(null);
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            setResetError(err.message || 'Failed to reset password.');
        }
    };

    const formatDate = (d) => {
        if (!d) return 'Never';
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    // Get current user ID from localStorage to prevent self-deactivation
    let currentUserId = null;
    try { currentUserId = JSON.parse(localStorage.getItem('thaman_user'))?.id; } catch (_) {}

    if (isLoading) return <Loader message="Loading users..." />;
    if (error) return <ErrorMessage message={error} />;

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Employee Dashboard</h1>

            {message && (
                <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg mb-4 text-sm font-medium">
                    {message}
                    <button onClick={() => setMessage('')} className="ml-2 text-green-500 hover:text-green-800">&times;</button>
                </div>
            )}

            {/* User Table */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4">All Users</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flyers</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                                    <td className="px-4 py-3 text-sm">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {user.role === 'admin' ? 'Admin' : 'Employee'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                            user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                                        }`}>
                                            {user.status === 'active' ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{user.flyer_count || 0}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(user.last_login_at)}</td>
                                    <td className="px-4 py-3 text-sm flex gap-2">
                                        <button
                                            onClick={() => { setResetTarget(user); setNewPassword(''); setConfirmNewPassword(''); setResetError(''); }}
                                            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                        >
                                            Reset Password
                                        </button>
                                        {user.id !== currentUserId && (
                                            <button
                                                onClick={() => handleToggleStatus(user)}
                                                className={`text-xs font-medium ${user.status === 'active' ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                                            >
                                                {user.status === 'active' ? 'Deactivate' : 'Activate'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create User Form */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Create New User</h2>
                {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">{formError}</div>}
                <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input type="text" required value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" required value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input type="password" required value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                        <input type="password" required value={newUser.confirmPassword} onChange={e => setNewUser({...newUser, confirmPassword: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="employee">Employee</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button type="submit" disabled={isCreating}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                            {isCreating ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Reset Password Modal */}
            {resetTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-lg font-bold mb-4">Reset Password for {resetTarget.name}</h3>
                        {resetError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg mb-3 text-sm">{resetError}</div>}
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                            <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setResetTarget(null)} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                            <button onClick={handleResetPassword} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Reset Password</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
